/**
 * FormValidator - Centralized form validation utilities
 *
 * Provides consistent validation patterns across the app:
 * - Common field validators (email, password, required)
 * - Validation result formatting
 * - React hook for form validation state
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { t as translate } from '../../i18n';

// ============================================================================
// Validators - Pure functions for field validation
// ============================================================================

/**
 * Check if a value is empty (null, undefined, or empty string)
 * @param {*} value - Value to check
 * @returns {boolean} True if empty
 */
export function isEmpty(value) {
  return value === null || value === undefined || String(value).trim() === '';
}

/**
 * Validate required field
 * @param {*} value - Field value
 * @param {string} [fieldName='This field'] - Field name for error message
 * @returns {string|null} Error message or null if valid
 */
export function validateRequired(value, fieldName = translate('common:validation.thisField')) {
  if (isEmpty(value)) {
    return translate('common:validation.required', { field: fieldName });
  }
  return null;
}

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {string|null} Error message or null if valid
 */
export function validateEmail(email) {
  if (isEmpty(email)) {
    return translate('common:validation.emailRequired');
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return translate('common:validation.emailInvalid');
  }
  return null;
}

/**
 * Validate password meets minimum requirements
 * @param {string} password - Password to validate
 * @param {number} [minLength=8] - Minimum length
 * @returns {string|null} Error message or null if valid
 */
export function validatePassword(password, minLength = 8) {
  if (isEmpty(password)) {
    return translate('common:validation.passwordRequired');
  }
  if (password.length < minLength) {
    return translate('common:validation.passwordMinLength', { count: minLength });
  }
  return null;
}

/**
 * Validate password confirmation matches
 * @param {string} password - Original password
 * @param {string} confirmPassword - Confirmation password
 * @returns {string|null} Error message or null if valid
 */
export function validatePasswordMatch(password, confirmPassword) {
  if (password !== confirmPassword) {
    return translate('common:validation.passwordsMismatch');
  }
  return null;
}

/**
 * Validate minimum length
 * @param {string} value - Value to validate
 * @param {number} minLength - Minimum length
 * @param {string} [fieldName='This field'] - Field name for error message
 * @returns {string|null} Error message or null if valid
 */
export function validateMinLength(value, minLength, fieldName = translate('common:validation.thisField')) {
  if (String(value).length < minLength) {
    return translate('common:validation.minLength', { field: fieldName, count: minLength });
  }
  return null;
}

/**
 * Validate maximum length
 * @param {string} value - Value to validate
 * @param {number} maxLength - Maximum length
 * @param {string} [fieldName='This field'] - Field name for error message
 * @returns {string|null} Error message or null if valid
 */
export function validateMaxLength(value, maxLength, fieldName = translate('common:validation.thisField')) {
  if (String(value).length > maxLength) {
    return translate('common:validation.maxLength', { field: fieldName, count: maxLength });
  }
  return null;
}

// ============================================================================
// Form Validation Helpers
// ============================================================================

/**
 * Run multiple validators on a single field
 * @param {*} value - Field value
 * @param {Array<Function>} validators - Array of validator functions
 * @returns {string|null} First error message or null if all valid
 */
export function validateField(value, validators) {
  for (const validator of validators) {
    const error = validator(value);
    if (error) return error;
  }
  return null;
}

/**
 * Validate entire form using a validation schema
 * @param {Object} values - Form values { fieldName: value }
 * @param {Object} schema - Validation schema { fieldName: [validators] | validatorFn }
 * @returns {{ errors: Object, isValid: boolean }} Validation result
 *
 * @example
 * const result = validateForm(
 *   { email: 'test@example.com', password: '123' },
 *   {
 *     email: [validateRequired, validateEmail],
 *     password: (v) => validatePassword(v, 8),
 *   }
 * );
 * // { errors: { password: 'Password must be at least 8 characters.' }, isValid: false }
 */
export function validateForm(values, schema) {
  const errors = {};

  for (const [field, validator] of Object.entries(schema)) {
    const value = values[field];
    let error = null;

    if (Array.isArray(validator)) {
      // Array of validators
      error = validateField(value, validator);
    } else if (typeof validator === 'function') {
      // Single validator function
      error = validator(value, values);
    }

    if (error) {
      errors[field] = error;
    }
  }

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
  };
}

// ============================================================================
// React Hook for Form Validation
// ============================================================================

/**
 * React hook for form validation state management
 *
 * @param {Object} initialValues - Initial form values
 * @param {Object} validationSchema - Validation schema
 * @param {Object} [options] - Options
 * @param {boolean} [options.validateOnChange=true] - Validate on value change
 * @param {boolean} [options.validateOnBlur=false] - Validate on blur
 * @returns {Object} Form state and handlers
 *
 * @example
 * const {
 *   values,
 *   errors,
 *   isValid,
 *   displayErrors,
 *   handleChange,
 *   handleSubmit,
 *   setFieldValue,
 * } = useFormValidation(
 *   { email: '', password: '' },
 *   {
 *     email: [validateEmail],
 *     password: (v) => validatePassword(v, 8),
 *   }
 * );
 */
export function useFormValidation(initialValues, validationSchema, options = {}) {
  const { validateOnChange = true } = options;

  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [displayErrors, setDisplayErrors] = useState(false);
  const [touched, setTouched] = useState({});

  // Use ref to avoid recreating validate when schema object reference changes
  const schemaRef = useRef(validationSchema);
  schemaRef.current = validationSchema;

  // Validate all fields
  const validate = useCallback(() => {
    const result = validateForm(values, schemaRef.current);
    setErrors(result.errors);
    setIsValid(result.isValid);
    return result;
  }, [values]);

  // Validate on values change
  useEffect(() => {
    if (validateOnChange) {
      validate();
    }
  }, [values, validateOnChange, validate]);

  // Set single field value
  const setFieldValue = useCallback((field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Handle input change (for TextInput onChangeText)
  const handleChange = useCallback((field) => (value) => {
    setFieldValue(field, value);
  }, [setFieldValue]);

  // Handle field blur
  const handleBlur = useCallback((field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  // Handle form submit
  const handleSubmit = useCallback((onSubmit) => {
    return () => {
      setDisplayErrors(true);
      const result = validate();
      if (result.isValid && onSubmit) {
        onSubmit(values);
      }
      return result.isValid;
    };
  }, [validate, values]);

  // Reset form
  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setIsValid(false);
    setDisplayErrors(false);
    setTouched({});
  }, [initialValues]);

  // Get error for a field (respects displayErrors flag)
  const getFieldError = useCallback((field) => {
    if (!displayErrors && !touched[field]) return null;
    return errors[field] || null;
  }, [displayErrors, touched, errors]);

  return {
    // State
    values,
    errors,
    isValid,
    displayErrors,
    touched,

    // Setters
    setValues,
    setFieldValue,
    setDisplayErrors,

    // Handlers
    handleChange,
    handleBlur,
    handleSubmit,
    validate,
    reset,

    // Helpers
    getFieldError,
  };
}

// ============================================================================
// Common Validation Schemas
// ============================================================================

/**
 * Common validation schemas for reuse
 */
export const commonSchemas = {
  /** Login form validation */
  login: {
    email: [validateEmail],
    password: [(v) => validateRequired(v, 'Password')],
  },

  /** Sign up form validation */
  signUp: {
    email: [validateEmail],
    password: [(v) => validatePassword(v, 8)],
  },

  /** Password reset validation */
  passwordReset: {
    password: [(v) => validatePassword(v, 8)],
    confirmPassword: (v, values) => validatePasswordMatch(values.password, v),
  },
};
